import { IChannel, IRoleProfile } from "@app/shared/models/common.model";
import { APPLICATION_OWNER_CHANNEL_NAME } from "@app/shared/services/role.service";
import { ChannelMeta } from "../../components/list/enrichment-list/enrichment-list.channels";

/** The query parameter a link names its channel in: `?channel=myais`. */
export const CHANNEL_PARAM = "channel";

/** The channel a link names, found. */
export interface LinkChannel {
  /** As a link writes it and `x-channel` sends it: the channel key. */
  key: string;
  /** As a person reads it. */
  name: string;
  channelId: number | null;
}

/** The role a link needs activated before it can be opened. */
export interface LinkRole {
  roleId: number;
  roleName: string;
  /** Sent to `/auth/role/activate` with the role. */
  channelId: number | null;
}

/**
 * What a link's `?channel=` comes to for this user.
 *
 *  - `none`       the link names no channel.
 *  - `ok`         the role on screen may work on it. `anyChannel`: the role
 *                 covers all channels and the link's is the one to send.
 *  - `switch`     another role of this user covers it; that one has to be
 *                 activated first.
 *  - `forbidden`  the channel exists, no role of this user covers it.
 *  - `unknown`    no such channel.
 */
export type ChannelLinkOutcome =
  | { kind: "none" }
  | { kind: "ok"; channel: LinkChannel; anyChannel: boolean }
  | {
      kind: "switch";
      channel: LinkChannel;
      role: LinkRole;
      anyChannel: boolean;
    }
  | { kind: "forbidden"; channel: LinkChannel }
  | { kind: "unknown"; value: string };

export interface ChannelLinkInput {
  value: string | null | undefined;
  /** The channels the pickers offer (`EnrichmentChannelDirectory`). */
  directory: readonly ChannelMeta[];
  /** False when that list could not be read: a channel is then never
   *  called unknown on the list's word alone. */
  directoryLoaded: boolean;
  profiles: readonly IRoleProfile[];
  selectedRoleId: number | null;
}

const same = (a?: string | null, b?: string | null): boolean =>
  !!a?.trim() &&
  !!b?.trim() &&
  a.trim().toLowerCase() === b.trim().toLowerCase();

const isAnyChannel = (channel: IChannel): boolean =>
  channel.channelName === APPLICATION_OWNER_CHANNEL_NAME;

/** A role on all channels: it carries the synthetic ApplicationOwner one. */
const coversAll = (profile: IRoleProfile): IChannel | undefined =>
  (profile.channels ?? []).find(isAnyChannel);

/** The channel of `profile` that is the link's, when the role is bound to it. */
const boundTo = (
  profile: IRoleProfile,
  channel: LinkChannel,
): IChannel | undefined =>
  (profile.channels ?? []).find(
    (c) =>
      !isAnyChannel(c) &&
      (c.channelId === channel.channelId ||
        same(c.channelKey, channel.key) ||
        same(c.channelName, channel.key) ||
        same(c.channelName, channel.name)),
  );

/** The channel a link value names: by key, then by sale name, then by the
 *  label a picker shows it under. Case does not matter (a key is lower-case,
 *  a role's channel name is not). */
function findChannel(
  input: ChannelLinkInput,
  value: string,
): LinkChannel | null {
  const row =
    input.directory.find((c) => same(c.value, value)) ??
    input.directory.find((c) => same(c.channelName, value)) ??
    input.directory.find((c) => same(c.label, value));
  if (row) {
    return {
      key: row.value?.trim() || row.channelName,
      name: row.label?.trim() || row.channelName,
      channelId: row.channelId,
    };
  }
  // A channel one of the user's roles is bound to, absent from the list
  // (the list is CMS channels in use).
  for (const profile of input.profiles) {
    for (const c of profile.channels ?? []) {
      if (isAnyChannel(c)) continue;
      if (same(c.channelKey, value) || same(c.channelName, value)) {
        return {
          key: c.channelKey?.trim() || c.channelName,
          name: c.channelName,
          channelId: c.channelId,
        };
      }
    }
  }
  return input.directoryLoaded
    ? null
    : { key: value.trim(), name: value.trim(), channelId: null };
}

/**
 * Decide what a link's `?channel=` comes to. Pure: the guard acts on it.
 *
 * The api decides the channel of a request by the role: a role bound to a
 * channel always works on that one (its `x-channel` is ignored), a role on
 * all channels works on the one it is sent. So a link is only honest when
 * the role on screen is bound to the link's channel, or covers all of them.
 * Otherwise the page would open on another channel's data without a word.
 *
 * A role bound to the link's channel is preferred over one on all channels
 * when the user has to switch: it is the narrower of the two.
 */
export function resolveChannelLink(
  input: ChannelLinkInput,
): ChannelLinkOutcome {
  const value = input.value?.trim();
  if (!value) return { kind: "none" };

  const channel = findChannel(input, value);
  if (!channel) return { kind: "unknown", value };

  // No role profile at all (the local bypass login has none): nothing to
  // judge by. The channel is sent and the api, which knows the token's
  // role, answers for it.
  if (input.profiles.length === 0) {
    return { kind: "ok", channel, anyChannel: true };
  }

  const selected = input.profiles.find(
    (p) => p.roleId === input.selectedRoleId,
  );
  if (selected && coversAll(selected)) {
    return { kind: "ok", channel, anyChannel: true };
  }
  if (selected && boundTo(selected, channel)) {
    return { kind: "ok", channel, anyChannel: false };
  }

  for (const profile of input.profiles) {
    const own = boundTo(profile, channel);
    if (own) {
      return {
        kind: "switch",
        channel,
        role: {
          roleId: profile.roleId,
          roleName: profile.roleName,
          channelId: own.channelId,
        },
        anyChannel: false,
      };
    }
  }
  for (const profile of input.profiles) {
    const all = coversAll(profile);
    if (all) {
      return {
        kind: "switch",
        channel,
        role: {
          roleId: profile.roleId,
          roleName: profile.roleName,
          channelId: all.channelId,
        },
        anyChannel: true,
      };
    }
  }
  return { kind: "forbidden", channel };
}
